import { Box, Grid, Typography, Stack } from "@mui/material";
import MzInput from "@markaaz/ui/MzInputControls";
import MzAlert from "@markaaz/ui/MzAlert";
import MzButton from "@markaaz/ui/MzButton";
import { useFormik } from "formik";
import * as yup from "yup";
import MzCheckbox from "@markaaz/ui/MzCheckbox";
import dayjs from "dayjs";

const sx = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "40px"
  },
  wrap: {
    display: "flex",
    padding: "40px",
    gap: "40px",
    backgroundColor: "common.white",
    width: "740px",
    height: "584px",
    borderRadius: "5px"
  },
  disabled: {
    "& input[disabled]": {
      backgroundColor: (theme) => theme.palette.darkBlue.c5
    }
  },
  subHeading: {
    color: (theme) => theme.palette.darkBlue.c100
  }
};

const validationSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  phone: yup
    .string()
    .when(["isSMSEnabled"], (isSMSEnabled, schema) => {
      const shouldRequirePhoneNumber = isSMSEnabled.includes(true);
      return shouldRequirePhoneNumber ? schema.required("Mobile number required for SMS notifications.") : schema;
    })
    .nullable()
    .notRequired()
});

export default function AccountInformation({ loading, userData, onSubmit, showNotification, onAlertClose }) {
  const initialValues = {
    userId: userData?.userId || "",
    userType: userData?.userType || "",
    firstName: userData?.firstName || "",
    lastName: userData?.lastName || "",
    company: userData?.epName || "",
    userRole: userData?.userRole || "",
    jobTitle: userData?.jobTitle || "",
    phone: userData?.phone || "",
    isEmailEnabled: userData?.isEmailEnabled || false,
    isSMSEnabled: userData?.isSMSEnabled || false
  };

  const formik = useFormik({
    initialValues,
    onSubmit: async () => {
      const { phone, ...newFormikValues } = formik.values;
      const submitValues = {
        ...newFormikValues,
        phone: formik.values.phone && formik.values.phone.replaceAll("-", "")
      };
      await onSubmit(submitValues);
    },
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true
  });

  const number = formik.values.phone?.replace(/\D/g, "");
  let formattedValue = "";
  if (number?.length > 0) {
    formattedValue = number.slice(0, 3);
    if (number.length > 3) {
      formattedValue += `-${number.slice(3, 6)}`;
      if (number.length > 6) {
        formattedValue += `-${number.slice(6, 10)}`;
      }
    }
  }

  return (
    <Box sx={sx.root}>
      <Box>
        <Typography sx={sx.subHeading} variant="h6">
          Manage Account Information
        </Typography>
      </Box>
      <Box sx={sx.wrap}>
        <Grid container spacing={6}>
          <Grid item xs={6}>
            <MzInput
              sx={sx.disabled}
              name="userId"
              label="User ID"
              disabled={true}
              fullWidth
              value={formik.values.userId}
              error={formik.touched.userId && !!formik.errors.userId}
              helperText={formik.touched.userId && formik.errors.userId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              sx={sx.disabled}
              name="userType"
              label="User Type"
              disabled={true}
              fullWidth
              value={formik.values.userType}
              error={formik.touched.userId && !!formik.errors.userType}
              helperText={formik.touched.userId && formik.errors.userType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              name="firstName"
              label="First Name"
              required
              fullWidth
              value={formik.values.firstName}
              error={formik.touched.firstName && !!formik.errors.firstName}
              helperText={formik.touched.firstName && formik.errors.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              name="lastName"
              label="Last Name"
              required
              fullWidth
              value={formik.values.lastName}
              error={formik.touched.lastName && !!formik.errors.lastName}
              helperText={formik.touched.lastName && formik.errors.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              sx={sx.disabled}
              name="company"
              label="Company"
              disabled={true}
              fullWidth
              value={formik.values.company}
              error={formik.touched.lastName && !!formik.errors.company}
              helperText={formik.touched.lastName && formik.errors.company}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              sx={sx.disabled}
              name="userRole"
              label="User Role"
              disabled={true}
              fullWidth
              value={formik.values.userRole}
              error={formik.touched.userId && !!formik.errors.userRole}
              helperText={formik.touched.userId && formik.errors.userRole}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              name="jobTitle"
              label="Job Title"
              fullWidth
              value={formik.values.jobTitle}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>

          <Grid item xs={6}>
            <MzInput
              name="phone"
              label="Phone Number"
              fullWidth
              InputProps={{ inputProps: { maxLength: 12 } }}
              value={formattedValue}
              error={formik.touched.phone && !!formik.errors.phone}
              helperText={
                (formik.touched.phone && formik.errors.phone) || " Mobile number required for SMS notifications."
              }
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2M" color="darkBlue.c100">
              Notifications Opt-In
            </Typography>
            <Box>
              <MzCheckbox
                name="isEmailEnabled"
                label="Email"
                checked={formik.values.isEmailEnabled}
                onChange={formik.handleChange}
              ></MzCheckbox>
              {userData?.emailOptInDate && (
                <Typography variant="caption" color="darkBlue.c70">
                  Updated: {dayjs(userData?.emailOptInDate).format("MM/DD/YY")}
                </Typography>
              )}
            </Box>
            <Box>
              <MzCheckbox
                name="isSMSEnabled"
                label="SMS"
                checked={formik.values.isSMSEnabled}
                onChange={(event) => {
                  formik.handleChange(event);
                  formik.setFieldTouched("phone", true, false);
                }}
              ></MzCheckbox>
              {userData?.smsOptInDate && (
                <Typography variant="caption" color="darkBlue.c70">
                  &nbsp;&nbsp; Updated: {dayjs(userData?.smsOptInDate).format("MM/DD/YY")}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Stack direction="row" gap={10} alignItems="center">
        <MzButton
          variant="contained"
          color="primary"
          size="large"
          onClick={formik.handleSubmit}
          loading={loading}
          disabled={!formik.isValid || formik.isSubmitting || !formik.dirty}
        >
          Update
        </MzButton>
        {showNotification.message && (
          <MzAlert
            sx={{ alignItems: "center" }}
            severity={showNotification.type === "success" ? "success" : "error"}
            onClose={() => {
              onAlertClose("aci");
            }}
          >
            {showNotification.message}
          </MzAlert>
        )}
      </Stack>
    </Box>
  );
}
